// src/app/time-attendance/components/payable-hours-report/services/payable-hours-report.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';

import {
  ApiPagedResponse,
  ApiResponse,
  EmployeeSearchDto,
  PayableHoursReportQuery,
  PayableHoursReportRowDto,
  SearchQuery,
  UserFilterValuesDto,
  WorkOrderNumberSearchDto,
  YywwDto,
  YywwQuery,
} from '../models/payable-hours-report.model';

@Injectable({ providedIn: 'root' })
export class PayableHoursReportService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private readonly http: HttpClient) {}

  getReport(query: PayableHoursReportQuery): Observable<PayableHoursReportRowDto[]> {
    const params = this.buildParams({
      yywwId: query.yywwId,
      companyIds: this.joinIds(query.companyIds),
      departmentIds: this.joinIds(query.departmentIds),
      payGroupIds: this.joinIds(query.payGroupIds),
      employeeIds: this.joinIds(query.employeeIds),
      WorkOrderNumberIds: this.joinIds(query.workOrderNumberIds),
    });

    return this.http
      .get<ApiResponse<PayableHoursReportRowDto[]>>(
        `${this.baseUrl}/api/payable-hours/get-report`,
        { params },
      )
      .pipe(map((res) => res.data ?? []));
  }

  getFilterValues(): Observable<UserFilterValuesDto> {
    return this.http
      .get<ApiResponse<UserFilterValuesDto>>(`${this.baseUrl}/api/users/filter-values`)
      .pipe(map((res) => res.data));
  }

  getYywws(options?: YywwQuery): Observable<ApiPagedResponse<YywwDto[]>> {
    const params = this.buildParams({
      searchTerm: options?.searchTerm,
      pageNumber: options?.pageNumber,
      pageSize: options?.pageSize,
    });

    return this.http.get<ApiPagedResponse<YywwDto[]>>(`${this.baseUrl}/api/yywws`, { params });
  }

  getAllYywwsByYear(year: number): Observable<YywwDto[]> {
    const pageSize = 100;

    return this.getAllPages<YywwDto>((pageNumber) =>
      this.getYywws({
        searchTerm: year,
        pageNumber,
        pageSize,
      }),
    ).pipe(
      map((weeks) =>
        weeks.sort((a, b) => {
          if (a.year !== b.year) return a.year - b.year;
          return a.week - b.week;
        }),
      ),
    );
  }

  searchEmployees(options: SearchQuery): Observable<EmployeeSearchDto[]> {
    const searchTerm = options.searchTerm?.trim();

    if (!searchTerm) {
      return of([]);
    }

    const pageSize = options.pageSize ?? 100;

    return this.getAllPages<EmployeeSearchDto>((pageNumber) => {
      const params = this.buildParams({
        searchTerm,
        pageNumber,
        pageSize,
      });

      return this.http.get<ApiPagedResponse<EmployeeSearchDto[]>>(`${this.baseUrl}/api/users`, {
        params,
      });
    }).pipe(map((rows) => rows.slice(0, 10)));
  }

  searchWorkOrderNumbers(options: SearchQuery): Observable<WorkOrderNumberSearchDto[]> {
    const searchTerm = options.searchTerm?.trim();

    if (!searchTerm) {
      return of([]);
    }

    const pageSize = options.pageSize ?? 20;

    return this.getAllPages<WorkOrderNumberSearchDto>((pageNumber) => {
      const params = this.buildParams({
        searchTerm,
        pageNumber,
        pageSize,
      });

      return this.http.get<ApiPagedResponse<WorkOrderNumberSearchDto[]>>(
        `${this.baseUrl}/api/work-order-numbers`,
        { params },
      );
    }).pipe(map((rows) => rows.slice(0, 10)));
  }

  private getAllPages<T>(
    fetchPage: (pageNumber: number) => Observable<ApiPagedResponse<T[]>>,
  ): Observable<T[]> {
    return fetchPage(1).pipe(
      expand((res) => {
        if (res.currentPage >= res.totalPages) {
          return of();
        }

        return fetchPage(res.currentPage + 1);
      }),
      map((res) => res.data ?? []),
      reduce((all, page) => [...all, ...page], [] as T[]),
    );
  }

  private buildParams(paramsObj: Record<string, unknown>): HttpParams {
    let params = new HttpParams();

    Object.entries(paramsObj).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return params;
  }

  private joinIds(ids: number[] | null | undefined): string | undefined {
    if (!ids?.length) {
      return undefined;
    }

    return ids.join(',');
  }
}